import {
  Box,
  IconButton,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  InputAdornment,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Search bar for the open conversation. Replaces the ChatHeader row while active so the
 * input gets the full width — on mobile there is no room to sit beside the avatar and
 * the action icons, and splitting the layouts would give the two viewports different
 * behaviour for no benefit.
 *
 * Presentational only: it owns no query state and performs no matching. The result count
 * appears once `resultCount` is passed, and reads as "N+" while further pages remain,
 * because the search endpoint is cursor-paginated and never reports a total.
 */
export const ChatMessageSearch = ({
  query,
  onQueryChange,
  onClose,
  resultCount,
  hasMore = false,
  isSearching = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const iconBtnSize = isMobile ? 40 : 32;
  const { t } = useTranslation();
  const inputRef = useRef(null);

  useEffect(() => {
    // Defer so the field is mounted and the header has settled before stealing focus
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, []);

  const showCount = resultCount !== undefined && !isSearching;

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <Box className="flex items-center gap-2 px-4 py-3">
      <IconButton
        onClick={onClose}
        size="small"
        aria-label={t('Close search')}
        sx={{
          width: iconBtnSize,
          height: iconBtnSize,
          flexShrink: 0,
          color: colors.text,
          opacity: 0.7,
          '&:hover': { opacity: 1 },
        }}
      >
        {isMobile ? (
          <ArrowBackIcon sx={{ fontSize: 22 }} />
        ) : (
          <CloseIcon sx={{ fontSize: 20 }} />
        )}
      </IconButton>

      <TextField
        inputRef={inputRef}
        fullWidth
        size="small"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t('Search messages')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: colors.text, opacity: 0.5, fontSize: 20 }} />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label={t('Clear search')}
                onClick={() => {
                  onQueryChange('');
                  inputRef.current?.focus();
                }}
                sx={{ color: colors.text, opacity: 0.5, '&:hover': { opacity: 1 } }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            height: 36,
            backgroundColor: colors.bgInput,
            borderRadius: '10px',
            '& fieldset': { border: 'none' },
          },
        }}
      />

      {showCount && (
        <Typography
          variant="caption"
          sx={{
            flexShrink: 0,
            pr: 0.5,
            color: resultCount === 0 ? colors.error : colors.text,
            opacity: resultCount === 0 ? 1 : 0.6,
            fontSize: '0.7rem',
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {resultCount === 0
            ? t('No matches')
            : `${resultCount}${hasMore ? '+' : ''}`}
        </Typography>
      )}
    </Box>
  );
};
